import { JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useOutletContext } from 'react-router-dom'

import { CancelAllOrdersModal } from '@pages/Trade/Portfolio/Modals/CancelAllOrdersModal'
import { useMarketDataEvents } from '@pages/Trade/hooks/useMarketDataEvents'
import { UseTradeLoginReturn } from '@pages/Trade/hooks/useTradeLogin'

import { useModal } from '@context/ModalsContext'

import { PageSection } from '@ui/components/PageSection'
import { Tab, Tabs, type TabsProps } from '@ui/components/Tabs'
import { SuitcaseIcon } from '@ui/icons/Suitcase.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import type { PageQuery } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'

import {
  MarketDataEventFormats,
  MarketDataEventTypes,
  type DxTradeOrderType,
  type MarketDataRequest,
} from '@model/DxTrade.types'

import { useActiveTab } from '@hooks/useActiveTab'

import { Box } from '@shared/components/Box'

import { type DxtradePositionEffect } from '../utils/constants'
import { BulkActionsRow } from './components/BulkActionsRow'
import { CardTrading } from './components/Card-trading'
import { FiltersRow, PendingFiltersType, PortfolioFiltersType } from './components/FiltersRow'
import { HistoryTab } from './components/HistoryTab'
import { MarketDataBridge, updateCurrentPricesFromMarketData } from './components/MarketDataBridge'
import { PendingTab } from './components/PendingTab'
import { PortfolioTradesTable } from './components/PortfolioTradesTable'
import { FixTradingWebSocketProvider } from './context/FixTradingWebSocketContext'
import { initializeCurrentPrices } from './hooks/useCurrentPricesStore'
import { useDxtradeInstruments } from './hooks/useDxtradeInstruments'
import { useDxtradeOpenPositions } from './hooks/useDxtradeOpenPositions'
import { PendingOrderWithTPSL, useDxtradePendingOrders } from './hooks/useDxtradePendingOrders'
import { useDxtradePortfolioTrades } from './hooks/useDxtradePortfolioTrades'
import { useHistoryOrdersWithLots } from './hooks/useHistoryOrdersWithLots'
import { useOpenTradesWithLots } from './hooks/useOpenTradesWithLots'
import { usePendingOrdersWithLots } from './hooks/usePendingOrdersWithLots'
import { usePortfolioActiveFilters } from './hooks/usePortfolioActiveFilters'
import { usePortfolioHistory } from './hooks/usePortfolioHistory'

import styles from './Portfolio.page.module.scss'

const MARKET_DATA_EVENT_TYPES: MarketDataRequest['eventTypes'] = [
  {
    type: MarketDataEventTypes.QUOTE,
    format: MarketDataEventFormats.COMPACT,
  },
]

export const initialPortfolioFiltersQuery = () => ({
  side: undefined,
  openTimeFrom: undefined,
  openTimeTo: undefined,
})

export const PortfolioPage = (): JSX.Element => {
  const { t } = useTranslation()

  const cancelAllOrdersModal = useModal(CancelAllOrdersModal, {
    onConfirm: () => {
      throw new Error('Function not implemented.')
    },
  })
  const { loading: tradeLoginLoading, selectedAccount } = useOutletContext<UseTradeLoginReturn>()

  const accountCode = useMemo(
    () =>
      selectedAccount
        ? {
            login: selectedAccount.platformOverview?.login,
            currency: selectedAccount.currency?.id,
          }
        : undefined,
    [selectedAccount]
  )

  const isAccountLoading = tradeLoginLoading

  const [openFilters, setOpenFilters] = useState<PortfolioFiltersType>(
    initialPortfolioFiltersQuery()
  )

  const [pendingFilters, setPendingFilters] = useState<PendingFiltersType>({})
  const isMobile = useWindowResize()
  const [isFiltersVisible, setIsFiltersVisible] = useState(!isMobile)

  const openPositionsQuery = useMemo(() => {
    const query: PageQuery = {}
    if (openFilters.side) query.side = openFilters.side
    if (openFilters.openTimeFrom) query.openTimeFrom = openFilters.openTimeFrom
    if (openFilters.openTimeTo) query.openTimeTo = openFilters.openTimeTo
    return Object.keys(query).length ? query : undefined
  }, [openFilters.side, openFilters.openTimeFrom, openFilters.openTimeTo])

  const { data: openPositionsData, isLoading: isOpenPosLoading } = useDxtradeOpenPositions(
    accountCode?.login ?? '',
    openPositionsQuery
  )
  const openCount = useMemo(() => openPositionsData?.positions?.length ?? 0, [openPositionsData])
  const {
    trades,
    allSymbols,
    isLoading: isTradesLoading,
  } = useDxtradePortfolioTrades(accountCode?.login ?? '', openPositionsData)
  const { data: marketDataEvents } = useMarketDataEvents({
    account: `default:${accountCode?.login}`,
    symbols: allSymbols,
    eventTypes: MARKET_DATA_EVENT_TYPES,
  })
  useEffect(() => {
    if (!marketDataEvents?.events?.length) return

    const batch: Record<string, { bid: number; ask: number }> = {}

    marketDataEvents.events.forEach((event) => {
      const bid = event.bid
      const ask = event.ask
      const symbol = event.symbol
      batch[symbol] = { bid, ask }
    })

    if (Object.keys(batch).length) {
      initializeCurrentPrices(batch)
    }
  }, [marketDataEvents])
  const { data: pendingOrdersResp, isLoading: isPendingLoading } = useDxtradePendingOrders(
    accountCode?.login ?? '',
    pendingFilters
  )
  const [pendingOrders, setPendingOrders] = useState<PendingOrderWithTPSL[]>([])
  const pendingCount = useMemo(() => pendingOrders?.length ?? 0, [pendingOrders])

  useEffect(() => {
    if (pendingOrdersResp !== undefined) {
      setPendingOrders(pendingOrdersResp)
    }
  }, [pendingOrdersResp])

  useEffect(() => {
    setIsFiltersVisible(!isMobile)
  }, [isMobile])

  const handleToggleFilters = useCallback(() => {
    if (!isMobile) return
    setIsFiltersVisible((prev) => !prev)
  }, [isMobile])

  const shouldShowFilters = !isMobile || isFiltersVisible

  const tabs: Tab[] = useMemo(
    () => [
      { id: 'open', path: 'open', label: `${t('Portfolio.Open')} (${openCount})` },
      { id: 'pending', path: 'pending', label: `${t('Pending')} (${pendingCount})` },
      { id: 'history', path: 'history', label: t('History') },
    ],
    [t, openCount, pendingCount]
  )

  const { activePath } = useActiveTab(tabs, { includeNested: true, initialIndex: 0 })
  const isOpenTab = activePath === '' || activePath === 'open'
  const tabProps = useMemo<TabsProps>(
    () => ({
      tabs,
      size: 'md',
    }),
    [tabs]
  )

  const shouldLoadHistory = activePath === 'history'

  const {
    historyFilters,
    setHistoryFilters,
    historyOrdersRaw,
    historyOrdersPage,
    historyPageMeta,
    isHistoryInitialLoading,
    handleHistoryPageChanged,
  } = usePortfolioHistory({
    accountLogin: accountCode?.login,
    isHistoryTabActive: shouldLoadHistory,
  })

  const currentTabFilters = useMemo(() => {
    if (activePath === 'pending') {
      return pendingFilters
    }
    if (activePath === 'history') {
      return historyFilters
    }
    return {}
  }, [activePath, pendingFilters, historyFilters])

  const instrumentSymbols = useMemo(() => {
    const symbols = new Set<string>()
    trades.forEach((trade) => {
      if (trade.symbol) symbols.add(trade.symbol)
    })
    pendingOrders.forEach((order) => {
      if (order.instrument) symbols.add(order.instrument)
    })
    historyOrdersRaw.forEach((order) => {
      if (order.instrument) symbols.add(order.instrument)
    })
    return Array.from(symbols)
  }, [trades, pendingOrders, historyOrdersRaw])

  const { instruments: dxtradeInstruments } = useDxtradeInstruments(
    accountCode?.login ?? '',
    instrumentSymbols
  )

  const historyOrders = useHistoryOrdersWithLots(historyOrdersPage, dxtradeInstruments)

  const preparedTrades = useOpenTradesWithLots(trades, dxtradeInstruments)
  const handleSetOrdersFilters = useCallback(
    (newVal: {
      side?: 'BUY' | 'SELL'
      ofType?: DxTradeOrderType | undefined
      positionEffect?: DxtradePositionEffect
    }) => {
      if (activePath === 'pending') {
        setPendingFilters((prev) => ({ ...prev, ...newVal }))
      } else if (activePath === 'history') {
        setHistoryFilters((prev) => ({ ...prev, ...newVal }))
      }
    },
    [activePath, setHistoryFilters]
  )

  const setOpenFiltersPartial = useCallback((newVal: Partial<typeof openFilters>) => {
    setOpenFilters((prev) => ({ ...prev, ...newVal }))
  }, [])
  const setOrdersBasicFilters = useCallback(
    (newVal: {
      side?: 'BUY' | 'SELL'
      ofType?: DxTradeOrderType | undefined
      positionEffect?: DxtradePositionEffect
    }) => {
      handleSetOrdersFilters(newVal)
    },
    [handleSetOrdersFilters]
  )
  const setOrdersDateFilters = useCallback(
    (newVal: { issuedFrom?: string; issuedTo?: string }) => {
      if (activePath === 'pending') {
        setPendingFilters((prev) => ({ ...prev, ...newVal }))
      } else if (activePath === 'history') {
        setHistoryFilters((prev) => ({ ...prev, ...newVal }))
      }
    },
    [activePath, setHistoryFilters]
  )

  const pendingOrdersWithLots = usePendingOrdersWithLots(pendingOrders, dxtradeInstruments)

  const { hasOpenActiveFilters, hasPendingActiveFilters, hasHistoryActiveFilters } =
    usePortfolioActiveFilters(openFilters, pendingFilters, historyFilters)

  return (
    <FixTradingWebSocketProvider>
      <div className={styles.portfolio}>
        <MarketDataBridge symbols={allSymbols} onTick={updateCurrentPricesFromMarketData} />
        <PageSection
          title={t('Portfolio.Portfolio')}
          tabData={!isMobile ? tabProps : undefined}
          onFilterClick={isMobile ? handleToggleFilters : undefined}
          icon={SuitcaseIcon}
        />

        {!isMobile ? (
          <Box flex className={styles.controlsRow}>
            <FiltersRow
              isOpenTab={isOpenTab}
              activePath={activePath}
              openFilters={openFilters}
              currentTabFilters={currentTabFilters}
              setOpenFilters={setOpenFiltersPartial}
              setOrdersBasicFilters={setOrdersBasicFilters}
              setOrdersDateFilters={setOrdersDateFilters}
              onFiltersChange={setOpenFilters}
            />
            <BulkActionsRow
              isOpenTab={isOpenTab}
              activePath={activePath}
              trades={preparedTrades}
              accountCurrency={accountCode?.currency}
              accountCode={accountCode?.login}
              onCancelAll={() => cancelAllOrdersModal.open()}
            />
          </Box>
        ) : (
          <>
            <div className={styles.mobileTabs}>
              <Tabs {...tabProps} />
            </div>
            {shouldShowFilters && (
              <div className={styles.mobileFilters}>
                <FiltersRow
                  isOpenTab={isOpenTab}
                  activePath={activePath}
                  openFilters={openFilters}
                  currentTabFilters={currentTabFilters}
                  setOpenFilters={setOpenFiltersPartial}
                  setOrdersBasicFilters={setOrdersBasicFilters}
                  setOrdersDateFilters={setOrdersDateFilters}
                  onFiltersChange={setOpenFilters}
                />
              </div>
            )}
            <div className={styles.mobileBulkActions}>
              <BulkActionsRow
                isOpenTab={isOpenTab}
                activePath={activePath}
                trades={preparedTrades}
                accountCurrency={accountCode?.currency}
                accountCode={accountCode?.login}
                onCancelAll={() => cancelAllOrdersModal.open()}
              />
            </div>
          </>
        )}

        {(activePath === '' || activePath === 'open') &&
          (isMobile ? (
            <CardTrading
              trades={preparedTrades}
              isLoading={isTradesLoading || isOpenPosLoading || isAccountLoading}
              accountCurrency={accountCode?.currency}
              accountCode={accountCode?.login}
              dxtradeInstruments={dxtradeInstruments}
              hasActiveFilters={hasOpenActiveFilters}
            />
          ) : (
            <PortfolioTradesTable
              trades={preparedTrades}
              isLoading={isTradesLoading || isOpenPosLoading || isAccountLoading}
              accountCurrency={accountCode?.currency}
              accountCode={accountCode?.login}
              dxtradeInstruments={dxtradeInstruments}
              hasActiveFilters={hasOpenActiveFilters}
            />
          ))}
        {activePath === 'pending' && (
          <PendingTab
            accountCurrency={accountCode?.currency}
            accountCode={accountCode?.login}
            orders={pendingOrdersWithLots}
            isLoading={isPendingLoading}
            dxtradeInstruments={dxtradeInstruments}
            hasActiveFilters={hasPendingActiveFilters}
          />
        )}
        {activePath === 'history' && (
          <>
            <HistoryTab
              orders={historyOrders}
              isLoading={isHistoryInitialLoading}
              accountCurrency={accountCode?.currency}
              accountCode={accountCode?.login}
              hasActiveFilters={hasHistoryActiveFilters}
            />
            {historyPageMeta && (
              <Pagination
                pageData={historyPageMeta}
                isLoading={isHistoryInitialLoading}
                onPageChanged={(pageIndex, pageSize) =>
                  handleHistoryPageChanged(pageIndex, pageSize)
                }
              />
            )}
          </>
        )}

        <Outlet />
      </div>
    </FixTradingWebSocketProvider>
  )
}
